import React, { useContext, useEffect, useState } from "react";
import { Form, Table, Alert, Modal } from "react-bootstrap";
import {
  organizerListScannersApi,
  organizerCreateScannerApi,
  organizerDeleteScannerApi,
  organizerAssignScannerWithTypesApi,
  organizerUnassignScannerApi,
  organizerAssignScannerToBarApi,
  organizerUnassignScannerFromBarApi,
  organizerListBarsApi,
  organizerListClubsApi,
  organizerListSellersApi,
  organizerCreateSellerApi,
  organizerDeleteSellerApi,
  organizerAssignSellerWithTypesApi,
  organizerUnassignSellerApi,
  organizerListEventsApi,
  organizerTicketTypesByEventApi,
  organizerGetSellerReferralLinkApi,
} from "../../api/api";
import { handleError } from "../../helpers/handleError";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import { OrganizerHeaderContext } from "../Layout";
import "../dashboard.css";
import editMobileIcon from "../../assets/image/wallets/editar-texto 2.svg";
import deleteMobileIcon from "../../assets/image/wallets/eliminar 1.svg";
import { toast } from "react-toastify";

const TAB_KEYS = {
  SCANNERS: "scanners",
  SELLERS: "sellers",
};

const ASSIGN_KIND = {
  EVENT: "event",
  RESERVA: "reserva",
};

// Builds the venue select options as two separately labelled groups (bars,
// then clubs) — never merged into one flat list. Group headers are
// non-selectable pseudo-options, reusing OrganizerPillSelect's existing
// `disabled` support instead of adding optgroup markup to the shared component.
const buildVenueOptions = (bars, clubs) => {
  const barOptions = (bars || [])
    .map((bar) => {
      const raw = bar?.id ?? bar?._id;
      if (!raw) return null;
      return { value: String(raw), label: bar?.name || "Local sin nombre" };
    })
    .filter(Boolean);
  const clubOptions = (clubs || [])
    .map((club) => {
      const raw = club?.id ?? club?._id;
      if (!raw) return null;
      return { value: String(raw), label: club?.name || "Club sin nombre" };
    })
    .filter(Boolean);

  const groups = [];
  if (barOptions.length > 0) {
    groups.push({ value: "__group-bares", label: "BARES", disabled: true });
    groups.push(...barOptions);
  }
  if (clubOptions.length > 0) {
    groups.push({ value: "__group-clubes", label: "CLUBES", disabled: true });
    groups.push(...clubOptions);
  }
  return groups;
};

const resolveEntryId = (entry) => {
  const raw =
    entry?._id ??
    entry?.id ??
    entry?.scannerId ??
    entry?.scannerid ??
    entry?.sellerId ??
    entry?.sellerid ??
    entry?.userId ??
    entry?.userid ??
    entry?.email ??
    entry?.name;
  return raw ? String(raw) : "";
};

const resolveEntryName = (entry) =>
  entry?.name ?? entry?.fullName ?? entry?.nombre ?? entry?.user?.name ?? entry?.username ?? "";

const resolveEntryEmail = (entry) =>
  entry?.email ?? entry?.correo ?? entry?.mail ?? entry?.user?.email ?? "";

const resolveEventId = (entry) =>
  entry?.event?._id ??
  entry?.event?.id ??
  entry?.eventId ??
  entry?.eventid ??
  entry?.assignedEventId ??
  "";

// Reads the scanner's currently ACTIVE bar assignment from the extended
// organizer scanner listing (barAssignments[], see backend commit closing
// T7b's revocation gap). A scanner shows at most one bar in this UI even
// though the backend allows several ACTIVE rows at once — the modal only
// edits a single "current" assignment, mirroring the event-assignment UX.
const resolveBarAssignmentId = (entry) => {
  const list = Array.isArray(entry?.barAssignments) ? entry.barAssignments : [];
  const raw = list[0]?.barId;
  return raw ? String(raw) : "";
};

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M11.5 2.5 13.5 4.5 5.5 12.5 3 13.5 4 11.5Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.5 13.5h-1v-1l8-8 1 1-8 8Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M4 6h8M5 6v6a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11 4.5V4a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
    <path d="M3 4.5 6 7.5l3-3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const Sellers = () => {
  const { setPageHeader } = useContext(OrganizerHeaderContext);

  useEffect(() => {
    setPageHeader({
      title: "Asignación de roles",
      subtitle: "Creá usuarios y asignalos a eventos con un rol específico.",
    });
  }, [setPageHeader]);

  const [scanners, setScanners] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [events, setEvents] = useState([]);
  const [bars, setBars] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [assign, setAssign] = useState({ eventId: "", barId: "" });
  const [assignKind, setAssignKind] = useState(ASSIGN_KIND.EVENT);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [allowAllTypes, setAllowAllTypes] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(TAB_KEYS.SCANNERS);
  const [selectedRows, setSelectedRows] = useState({
    [TAB_KEYS.SCANNERS]: [],
    [TAB_KEYS.SELLERS]: [],
  });
  const [mobileExpandedId, setMobileExpandedId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [modalForm, setModalForm] = useState({ name: "", email: "", password: "", eventId: "", barId: "" });
  const [modalAssignKind, setModalAssignKind] = useState(ASSIGN_KIND.EVENT);
  const [modalAllowAllTypes, setModalAllowAllTypes] = useState(true);
  const [modalTicketTypes, setModalTicketTypes] = useState([]);
  const [modalSelectedTypeIds, setModalSelectedTypeIds] = useState([]);
  const [modalEntryId, setModalEntryId] = useState("");
  const [modalOriginalEventId, setModalOriginalEventId] = useState("");
  const [modalOriginalBarId, setModalOriginalBarId] = useState("");
  const [modalSaving, setModalSaving] = useState(false);
  const [linkLoadingId, setLinkLoadingId] = useState("");

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSelectedRows((prev) => ({ ...prev, [tab]: [] }));
    setMobileExpandedId(null);
  };

  const handleModalClose = () => {
    setIsEditModalOpen(false);
    setModalForm({ name: "", email: "", password: "", eventId: "", barId: "" });
    setModalAssignKind(ASSIGN_KIND.EVENT);
    setModalAllowAllTypes(true);
    setModalSelectedTypeIds([]);
    setModalTicketTypes([]);
    setModalEntryId("");
    setModalOriginalEventId("");
    setModalOriginalBarId("");
    setModalSaving(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [sellersRes, scannersRes, ev, barsRes, clubsRes] = await Promise.all([
        organizerListSellersApi(),
        organizerListScannersApi(),
        organizerListEventsApi(),
        organizerListBarsApi(),
        organizerListClubsApi(),
      ]);
      const sellerList = Array.isArray(sellersRes?.data?.info) ? sellersRes.data.info : [];
      const scannerList = Array.isArray(scannersRes?.data?.info) ? scannersRes.data.info : [];
      setSellers(sellerList);
      setScanners(scannerList);
      setSelectedRows({
        [TAB_KEYS.SCANNERS]: [],
        [TAB_KEYS.SELLERS]: [],
      });
      setEvents(Array.isArray(ev?.data?.info) ? ev.data.info : []);
      setBars(Array.isArray(barsRes?.data?.info) ? barsRes.data.info : []);
      setClubs(Array.isArray(clubsRes?.data?.info) ? clubsRes.data.info : []);
      setMobileExpandedId(null);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        setTicketTypes([]);
        setSelectedTypeIds([]);
        if (!assign.eventId) return;
        const res = await organizerTicketTypesByEventApi({ eventid: assign.eventId });
        const list = Array.isArray(res?.data?.info) ? res.data.info : [];
        setTicketTypes(list);
      } catch (e) {
        console.error(e);
      }
    };
    fetchTypes();
  }, [assign.eventId]);

  const isScannerTab = activeTab === TAB_KEYS.SCANNERS;
  const isReservaAssign = isScannerTab && assignKind === ASSIGN_KIND.RESERVA;

  const onCreate = async (e) => {
    e.preventDefault();
    setError("");
    if (
      assign.eventId &&
      !allowAllTypes &&
      selectedTypeIds.length === 0
    ) {
      setError('Seleccioná al menos un tipo de entrada o activá "Permitir todas".');
      return;
    }
    try {
      setLoading(true);
      const createApi =
        activeTab === TAB_KEYS.SCANNERS ? organizerCreateScannerApi : organizerCreateSellerApi;
      const formSnapshot = { ...form };
      const createRes = await createApi(formSnapshot);
      const createdId = resolveEntryId(createRes?.data?.info);
      // The Evento/Reserva toggle is a VISUAL FILTER only (reservations-
      // scanner-parity T7c). A scanner can hold an event assignment AND a
      // bar assignment at the same time, so both sides are saved
      // independently whenever they carry a value, regardless of which tab
      // is currently visible.
      const needsBarAssignment = Boolean(assign.barId);
      const needsEventAssignment = Boolean(assign.eventId);
      if (needsBarAssignment || needsEventAssignment) {
        let entryId = createdId;
        if (!entryId) {
          const listApi =
            activeTab === TAB_KEYS.SCANNERS ? organizerListScannersApi : organizerListSellersApi;
          const listRes = await listApi();
          const list = Array.isArray(listRes?.data?.info) ? listRes.data.info : [];
          const targetEmail = String(formSnapshot.email || "").toLowerCase();
          const match = list.find(
            (item) => resolveEntryEmail(item).toLowerCase() === targetEmail
          );
          entryId = resolveEntryId(match);
        }
        if (entryId) {
          if (needsBarAssignment) {
            await organizerAssignScannerToBarApi({ barId: assign.barId, scannerId: entryId });
          }
          if (needsEventAssignment) {
            const assignApi =
              activeTab === TAB_KEYS.SCANNERS
                ? organizerAssignScannerWithTypesApi
                : organizerAssignSellerWithTypesApi;
            const payload = {
              eventId: assign.eventId,
              [activeTab === TAB_KEYS.SCANNERS ? "scannerId" : "sellerId"]: entryId,
            };
            if (allowAllTypes) {
              await assignApi({ ...payload, allowAll: true });
            } else {
              await assignApi({ ...payload, ticketTypeIds: selectedTypeIds });
            }
          }
        }
      }
      setForm({ name: "", email: "", password: "" });
      setAssign({ eventId: "", barId: "" });
      setAssignKind(ASSIGN_KIND.EVENT);
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Error al crear usuario.");
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (id) => {
    if (!id || !window.confirm("¿Eliminar usuario?")) return;
    try {
      setLoading(true);
      const deleteApi =
        activeTab === TAB_KEYS.SCANNERS ? organizerDeleteScannerApi : organizerDeleteSellerApi;
      await deleteApi(id);
      await load();
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleRowSelection = (entryId) => {
    if (!entryId) return;
    setSelectedRows((prev) => {
      const current = prev[activeTab] ?? [];
      const nextSelection = current.includes(entryId)
        ? current.filter((value) => value !== entryId)
        : [...current, entryId];
      return { ...prev, [activeTab]: nextSelection };
    });
  };

  const currentList = activeTab === TAB_KEYS.SCANNERS ? scanners : sellers;
  const activeSelection = selectedRows[activeTab] ?? [];
  const resolvedIds = currentList.map((entry) => resolveEntryId(entry)).filter(Boolean);
  const allSelected =
    resolvedIds.length > 0 && resolvedIds.every((id) => activeSelection.includes(id));
  const toggleAllRows = () => {
    if (allSelected) {
      setSelectedRows((prev) => ({ ...prev, [activeTab]: [] }));
      return;
    }
    setSelectedRows((prev) => ({ ...prev, [activeTab]: resolvedIds }));
  };

  const toggleMobileEntry = (entryKey) => {
    if (!entryKey) return;
    setMobileExpandedId((prev) => (prev === entryKey ? null : entryKey));
  };

  const getStatusVariant = (entry) => {
    const rawStatus = String(entry?.status ?? entry?.estado ?? entry?.state ?? "").toLowerCase();
    if (rawStatus.includes("inactivo") || rawStatus.includes("inactive")) return "inactive";
    if (rawStatus.includes("activo") || rawStatus.includes("active")) return "active";
    if (entry?.isActive === false || entry?.active === false) return "inactive";
    if (entry?.isActive === true || entry?.active === true) return "active";
    return "active";
  };

  const getEventName = (entry) =>
    entry?.event?.name || entry?.eventName || entry?.evento || entry?.assignedEventName || "Sin evento";

  const getSalesSummary = (entry) => {
    const summary = entry?.salesSummary || {};
    return {
      orders: Number(summary.orders || 0),
      ticketsSold: Number(summary.ticketsSold || 0),
      grossAmount: Number(summary.grossAmount || 0),
      netAmount: Number(summary.netAmount || 0),
      discountAmount: Number(summary.discountAmount || 0),
      webOrders: Number(summary.webOrders || 0),
      appOrders: Number(summary.appOrders || 0),
    };
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(Number(value || 0));

  const copyTextToClipboard = async (value) => {
    const text = String(value || "");
    if (!text) throw new Error("EMPTY_TEXT");
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const input = document.createElement("textarea");
    input.value = text;
    input.setAttribute("readonly", "readonly");
    input.style.position = "absolute";
    input.style.left = "-9999px";
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
  };

  const handleCopyReferralLink = async (entry) => {
    if (activeTab !== TAB_KEYS.SELLERS) return;
    const sellerId = resolveEntryId(entry);
    const eventId = resolveEventId(entry);
    if (!sellerId) return;
    if (!eventId) {
      try { toast.info("Asigná un evento al vendedor primero."); } catch (_) {}
      return;
    }
    try {
      setLinkLoadingId(sellerId);
      const response = await organizerGetSellerReferralLinkApi(sellerId, eventId);
      const url = response?.data?.info?.url;
      if (!url) throw new Error("MISSING_REFERRAL_URL");
      await copyTextToClipboard(url);
      try { toast.success("Link de referido copiado."); } catch (_) {}
    } catch (e) {
      console.error(e);
      handleError(e);
    } finally {
      setLinkLoadingId("");
    }
  };

  const isModalReservaAssign = isScannerTab && modalAssignKind === ASSIGN_KIND.RESERVA;

  const handleModalSave = async () => {
    if (!modalEntryId) return;
    if (
      modalForm.eventId &&
      !modalAllowAllTypes &&
      modalSelectedTypeIds.length === 0
    ) {
      window.alert('Selecciona al menos un tipo de entrada o activa "Permitir todas".');
      return;
    }
    const idKey = activeTab === TAB_KEYS.SCANNERS ? "scannerId" : "sellerId";

    const assignApi =
      activeTab === TAB_KEYS.SCANNERS
        ? organizerAssignScannerWithTypesApi
        : organizerAssignSellerWithTypesApi;
    const unassignApi =
      activeTab === TAB_KEYS.SCANNERS ? organizerUnassignScannerApi : organizerUnassignSellerApi;

    try {
      setModalSaving(true);

      // Bar/reserva side. The Evento/Reserva toggle is a VISUAL FILTER
      // only (reservations-scanner-parity T7c) — a scanner can hold an
      // event assignment AND a bar assignment at the same time, so this
      // runs independently of which tab is currently visible, and never
      // touches the event side. Backend does NOT revoke the previous bar
      // row when assigning a new one (assign-bar only upserts the row
      // matching the new barId — see
      // controllers/Organizer/scanners.controller.js assignScannerToBar).
      // The client must explicitly unassign the old bar before/while
      // assigning the new one, otherwise both stay ACTIVE at once.
      if (isScannerTab) {
        if (modalOriginalBarId && modalOriginalBarId !== modalForm.barId) {
          await organizerUnassignScannerFromBarApi({ barId: modalOriginalBarId, [idKey]: modalEntryId });
        }
        if (modalForm.barId) {
          await organizerAssignScannerToBarApi({ barId: modalForm.barId, [idKey]: modalEntryId });
        }
      }

      // Event side. Also independent of the visible tab and never touches
      // the bar side.
      if (!modalForm.eventId) {
        if (modalOriginalEventId) {
          await unassignApi({ eventId: modalOriginalEventId, [idKey]: modalEntryId });
        }
      } else {
        if (modalOriginalEventId && modalOriginalEventId !== modalForm.eventId) {
          await unassignApi({ eventId: modalOriginalEventId, [idKey]: modalEntryId });
        }
        const payload = { eventId: modalForm.eventId, [idKey]: modalEntryId };
        if (modalAllowAllTypes) {
          await assignApi({ ...payload, allowAll: true });
        } else {
          await assignApi({ ...payload, ticketTypeIds: modalSelectedTypeIds });
        }
      }

      await load();
      handleModalClose();
      try { toast.success("Asignación actualizada."); } catch (_) {}
    } catch (e) {
      console.error(e);
      handleError(e);
    } finally {
      setModalSaving(false);
    }
  };

  const venueOptions = buildVenueOptions(bars, clubs);

  const formTitle = activeTab === TAB_KEYS.SCANNERS ? "escáner" : "vendedor";
  const tableTitle = activeTab === TAB_KEYS.SCANNERS ? "Mis escáneres" : "Mis vendedores";
  const tableSubtitle = "GESTIONÁ ACCESOS Y ELIMINÁ PERFILES CUANDO SEA NECESARIO.";
  const tableCountLabel =
    activeTab === TAB_KEYS.SCANNERS ? "escáner" : "vendedor";
  const tableCountLabelPlural =
    activeTab === TAB_KEYS.SCANNERS ? "escáneres" : "vendedores";
  const emptyListLabel =
    activeTab === TAB_KEYS.SCANNERS ? "escáneres" : "vendedores";
  const countLabelText = `${currentList.length} ${
    currentList.length === 1 ? tableCountLabel : tableCountLabelPlural
  }`;

  useEffect(() => {
    const fetchModalTypes = async () => {
      if (!modalForm.eventId) {
        setModalTicketTypes([]);
        setModalSelectedTypeIds([]);
        return;
      }
      try {
        const res = await organizerTicketTypesByEventApi({ eventid: modalForm.eventId });
        const list = Array.isArray(res?.data?.info) ? res.data.info : [];
        setModalTicketTypes(list);
        setModalSelectedTypeIds([]);
      } catch (e) {
        console.error(e);
        setModalTicketTypes([]);
        setModalSelectedTypeIds([]);
      }
    };
    fetchModalTypes();
  }, [modalForm.eventId]);

  const setModalDataFromEntry = (entry) => {
    const entryId = resolveEntryId(entry);
    const eventId = resolveEventId(entry);
    const barId = resolveBarAssignmentId(entry);
    setModalForm({
      name: resolveEntryName(entry) || "",
      email: resolveEntryEmail(entry) || "",
      password: "",
      eventId: eventId || "",
      barId: barId || "",
    });
    // The toggle now only decides which tab opens by default (visual
    // filter, T7c) — it no longer hides the other side's assignment. A
    // scanner with an event assignment keeps opening on the "Evento" tab;
    // one with only a bar assignment opens pre-selected on "Reserva". Either
    // way, both assignments are loaded into modalForm above, and the
    // toggle buttons carry a dot indicator whenever the non-visible side
    // also holds an assignment (see roles-kind-toggle__dot below).
    setModalAssignKind(!eventId && barId ? ASSIGN_KIND.RESERVA : ASSIGN_KIND.EVENT);
    setModalAllowAllTypes(true);
    setModalSelectedTypeIds([]);
    setModalTicketTypes([]);
    setModalEntryId(entryId);
    setModalOriginalEventId(eventId || "");
    setModalOriginalBarId(barId || "");
    setIsEditModalOpen(true);
  };

  const openEditModal = (entryParam) => {
    let entry = entryParam;
    if (!entry) {
      if (activeSelection.length === 0) return;
      const entryId = activeSelection[0];
      entry = currentList.find((item) => resolveEntryId(item) === entryId);
    }
    if (!entry) return;
    setModalDataFromEntry(entry);
  };

  const handleMobileEdit = (entry) => {
    if (!entry) return;
    const entryId = resolveEntryId(entry);
    if (entryId) {
      setSelectedRows((prev) => ({ ...prev, [activeTab]: [entryId] }));
    }
    setModalDataFromEntry(entry);
  };

  if (loading) return <SpinLoading />;

  return (
    <section className="roles-page-root">
      <div className="roles-tabs-bar" role="tablist">
        <button
          type="button"
          className={`roles-tab ${activeTab === TAB_KEYS.SCANNERS ? "is-active" : ""}`}
          onClick={() => handleTabSwitch(TAB_KEYS.SCANNERS)}
          role="tab"
          aria-selected={activeTab === TAB_KEYS.SCANNERS}
        >
          ESCÁNERES
        </button>
        <button
          type="button"
          className={`roles-tab ${activeTab === TAB_KEYS.SELLERS ? "is-active" : ""}`}
          onClick={() => handleTabSwitch(TAB_KEYS.SELLERS)}
          role="tab"
          aria-selected={activeTab === TAB_KEYS.SELLERS}
        >
          VENDEDORES
        </button>
      </div>

      <div className="roles-main-card">
        {error && (
          <Alert variant="danger" className="roles-page-error">
            {error}
          </Alert>
        )}

        <div className="roles-main-grid">
          <div className="roles-panel roles-panel--form">
            <div className="roles-panel-heading">
              <h3 className="roles-panel-title">Crear nuevo {formTitle}</h3>
            </div>
            <Form onSubmit={onCreate} className="roles-form">
              <Form.Control
                className="roles-input"
                placeholder="Nombre"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <Form.Control
                className="roles-input"
                placeholder="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <Form.Control
                className="roles-input"
                placeholder="Contraseña"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
              {isScannerTab && (
                <div className="roles-kind-toggle" role="group" aria-label="Tipo de asignación">
                  <button
                    type="button"
                    className={`roles-kind-toggle__btn ${assignKind === ASSIGN_KIND.EVENT ? "is-active" : ""}`}
                    onClick={() => setAssignKind(ASSIGN_KIND.EVENT)}
                  >
                    Evento
                    {Boolean(assign.eventId) && (
                      <span className="roles-kind-toggle__dot" title="Con evento asignado" />
                    )}
                  </button>
                  <button
                    type="button"
                    className={`roles-kind-toggle__btn ${assignKind === ASSIGN_KIND.RESERVA ? "is-active" : ""}`}
                    onClick={() => setAssignKind(ASSIGN_KIND.RESERVA)}
                  >
                    Reserva
                    {Boolean(assign.barId) && (
                      <span className="roles-kind-toggle__dot" title="Con local asignado" />
                    )}
                  </button>
                </div>
              )}

              {isReservaAssign ? (
                <OrganizerPillSelect
                  placeholder="Asignar evento o reserva"
                  value={assign.barId}
                  onChange={(val) => setAssign((prev) => ({ ...prev, barId: val ? String(val) : "" }))}
                  options={venueOptions}
                  className="roles-input roles-select"
                />
              ) : (
                <OrganizerPillSelect
                  placeholder="Asignar evento o reserva"
                  value={assign.eventId}
                  onChange={(val) => setAssign((prev) => ({ ...prev, eventId: val ? String(val) : "" }))}
                  options={events
                    .map((ev) => {
                      const raw = ev?.id ?? ev?._id ?? ev?.eventid;
                      if (!raw) return null;
                      return { value: String(raw), label: ev?.name || "Evento sin nombre" };
                    })
                    .filter(Boolean)}
                  className="roles-input roles-select"
                />
              )}

              {!isReservaAssign && (
                <label className="roles-toggle">
                  <input
                    type="checkbox"
                    checked={allowAllTypes}
                    onChange={(e) => setAllowAllTypes(e.target.checked)}
                  />
                  <span className="roles-toggle__track">
                    <span className="roles-toggle__thumb" />
                  </span>
                  <span className="roles-toggle__label">
                    {activeTab === TAB_KEYS.SCANNERS
                      ? "Permitir validar todas las entradas"
                      : "Permitir vender todos los tipos"}
                  </span>
                </label>
              )}

              {!isReservaAssign && assign.eventId && !allowAllTypes && (
                <div className="roles-ticket-types">
                  {ticketTypes.length === 0 ? (
                    <p className="roles-ticket-types__empty">
                      No hay tipos de entradas para este evento
                    </p>
                  ) : (
                    ticketTypes.map((tt) => {
                      const rawId = String(tt._id ?? tt.id ?? tt?.ticketid ?? tt?.typeid);
                      if (!rawId) return null;
                      return (
                        <label key={rawId} className="roles-ticket-type">
                          <input
                            type="checkbox"
                            checked={selectedTypeIds.includes(rawId)}
                            onChange={(e) => {
                              const id = rawId;
                              setSelectedTypeIds((prev) =>
                                e.target.checked
                                  ? [...new Set([...prev, id])]
                                  : prev.filter((value) => value !== id)
                              );
                            }}
                          />
                          <span>
                            {tt.name}
                            {tt.zone || tt.gate
                              ? ` · ${[tt.zone, tt.gate].filter(Boolean).join(" / ")}`
                              : ""}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              )}

              <div className="roles-form-actions">
                <button type="submit" className="roles-btn">
                  Crear
                </button>
              </div>
            </Form>
          </div>

          <div className="roles-panel roles-panel--table">
            <div className="roles-panel-heading roles-panel-heading--table">
              <div>
                <h3 className="roles-panel-title">{tableTitle}</h3>
                <p className="roles-panel-subtitle">{tableSubtitle}</p>
              </div>
              <span className="roles-panel-count">{countLabelText}</span>
            </div>

            <div className="roles-table-wrapper">
              <Table responsive className="roles-table align-middle">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAllRows}
                        aria-label="Seleccionar todos"
                      />
                    </th>
                    <th>NOMBRE</th>
                    <th>EMAIL</th>
                    <th>EVENTO</th>
                    <th>ESTADO</th>
                  </tr>
                </thead>
                <tbody>
                  {currentList.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="roles-table__empty">
                        Aún no hay {emptyListLabel}
                      </td>
                    </tr>
                  ) : (
                    currentList.map((entry) => {
                      const entryId = resolveEntryId(entry);
                      const statusVariant = getStatusVariant(entry);
                      const statusLabel = statusVariant === "active" ? "ACTIVO" : "INACTIVO";
                      const eventLabel = getEventName(entry);
                      const hasEvent = eventLabel && eventLabel !== "Sin evento";
                      const salesSummary = getSalesSummary(entry);
                      const isSellerRow = activeTab === TAB_KEYS.SELLERS;
                      const metricsLabel = `${salesSummary.orders} ventas · ${salesSummary.ticketsSold} tickets · ${formatCurrency(salesSummary.netAmount)}`;
                      return (
                        <tr
                          key={entryId || entry.email}
                          onDoubleClick={() => openEditModal(entry)}
                          style={{ cursor: entryId ? "pointer" : "default" }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={activeSelection.includes(entryId)}
                              onChange={() => toggleRowSelection(entryId)}
                              aria-label={`Seleccionar ${resolveEntryName(entry) || "registro"}`}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td>{resolveEntryName(entry) || "Sin nombre"}</td>
                          <td>{resolveEntryEmail(entry) || "sin email"}</td>
                          <td>
                            <div className="roles-event-cell">
                              <button
                                type="button"
                                className="roles-event-btn"
                                onClick={() => openEditModal(entry)}
                                disabled={!entryId}
                                title={hasEvent ? "Cambiar evento" : "Asignar evento"}
                              >
                                {eventLabel}
                              </button>
                              {isSellerRow && (
                                <>
                                  <span className="roles-sales-summary">{metricsLabel}</span>
                                  <button
                                    type="button"
                                    className="roles-link-action"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopyReferralLink(entry);
                                    }}
                                    disabled={!entryId || !resolveEventId(entry) || linkLoadingId === entryId}
                                  >
                                    {linkLoadingId === entryId ? "Generando..." : "Copiar link"}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`roles-status roles-status--${statusVariant}`}>
                              <span className="roles-status__dot" />
                              <span>{statusLabel}</span>
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>

            <div className="roles-mobile-list">
              {currentList.length === 0 ? (
                <p className="roles-mobile-empty">Aún no hay {emptyListLabel}</p>
              ) : (
                currentList.map((entry, index) => {
                  const entryId = resolveEntryId(entry);
                  const statusVariant = getStatusVariant(entry);
                  const statusLabel = statusVariant === "active" ? "Activo" : "Inactivo";
                  const salesSummary = getSalesSummary(entry);
                  const isSellerRow = activeTab === TAB_KEYS.SELLERS;
                  const entryKey = entryId || `${activeTab}-${index}`;
                  const isOpen = mobileExpandedId === entryKey;
                  return (
                    <article
                      key={`mobile-${entryKey}`}
                      className={`roles-mobile-entry ${isOpen ? "is-open" : ""}`}
                    >
                      <button
                        type="button"
                        className="roles-mobile-entry__toggle"
                        onClick={() => toggleMobileEntry(entryKey)}
                        aria-expanded={isOpen}
                      >
                        <span className="roles-mobile-entry__toggle-icon" aria-hidden="true">
                          <ArrowDownIcon />
                        </span>
                        <span className="roles-mobile-entry__toggle-label">
                          {resolveEntryEmail(entry) || "sin email"}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="roles-mobile-entry__body">
                          <dl className="roles-mobile-entry__details">
                            <div>
                              <dt className="roles-mobile-entry__label">Nombre</dt>
                              <dd>{resolveEntryName(entry) || "Sin nombre"}</dd>
                            </div>
                            <div>
                              <dt className="roles-mobile-entry__label">Contraseña</dt>
                              <dd>-</dd>
                            </div>
                            <div>
                              <dt className="roles-mobile-entry__label">Evento</dt>
                              <dd>{getEventName(entry)}</dd>
                            </div>
                            {isSellerRow && (
                              <div>
                                <dt className="roles-mobile-entry__label">Ventas</dt>
                                <dd>{`${salesSummary.orders} / ${salesSummary.ticketsSold} / ${formatCurrency(salesSummary.netAmount)}`}</dd>
                              </div>
                            )}
                            <div>
                              <dt className="roles-mobile-entry__label">Estado</dt>
                              <dd
                                className={`roles-mobile-entry__state roles-mobile-entry__state--${statusVariant}`}
                              >
                                {statusLabel}
                              </dd>
                            </div>
                          </dl>
                          <div className="roles-mobile-entry__actions">
                            {isSellerRow && (
                              <button
                                type="button"
                                className="roles-mobile-entry__link-button"
                                onClick={() => handleCopyReferralLink(entry)}
                                disabled={!entryId || !resolveEventId(entry) || linkLoadingId === entryId}
                              >
                                {linkLoadingId === entryId ? "Generando..." : "Copiar link"}
                              </button>
                            )}
                            <button
                              type="button"
                              className="roles-mobile-entry__icon-button"
                              onClick={() => handleMobileEdit(entry)}
                              disabled={!entryId}
                              aria-label="Editar"
                            >
                              <img src={editMobileIcon} alt="" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="roles-mobile-entry__icon-button"
                              onClick={() => onDelete(entryId)}
                              disabled={!entryId}
                              aria-label="Eliminar"
                            >
                              <img src={deleteMobileIcon} alt="" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })
              )}
            </div>

            <div className="roles-table-actions">
              <button
                type="button"
                className="roles-action"
                disabled={activeSelection.length === 0}
                onClick={openEditModal}
              >
                <span className="roles-action__icon" aria-hidden="true">
                  <EditIcon />
                </span>
                Editar
              </button>
              <button
                type="button"
                className="roles-action"
                disabled={activeSelection.length === 0}
                onClick={() => onDelete(activeSelection[0])}
              >
                <span className="roles-action__icon" aria-hidden="true">
                  <TrashIcon />
                </span>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* react-bootstrap portals the Modal to document.body by default, which
          sits outside the .theme-minimal wrapper. Since our design tokens are
          CSS custom properties declared on .theme-minimal, they don't
          inherit into a portal mounted outside that subtree (e.g. the
          Evento/Reserva toggle rendered unstyled). Mounting the portal inside
          .theme-minimal keeps token inheritance intact for all modal content. */}
      <Modal
        show={isEditModalOpen}
        onHide={handleModalClose}
        centered
        container={() => document.querySelector(".theme-minimal") || document.body}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {activeTab === TAB_KEYS.SCANNERS ? "Editar escáner" : "Editar vendedor"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="roles-modal-body">
          <Form className="roles-modal-form">
            <Form.Control
              className="roles-input"
              placeholder="Nombre"
              value={modalForm.name}
              onChange={(e) => setModalForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <Form.Control
              className="roles-input"
              placeholder="Email"
              type="email"
              value={modalForm.email}
              onChange={(e) => setModalForm((prev) => ({ ...prev, email: e.target.value }))}
            />
            <Form.Control
              className="roles-input"
              placeholder="Contraseña"
              type="password"
              value={modalForm.password}
              onChange={(e) => setModalForm((prev) => ({ ...prev, password: e.target.value }))}
            />
            {isScannerTab && (
              <div className="roles-kind-toggle" role="group" aria-label="Tipo de asignación">
                <button
                  type="button"
                  className={`roles-kind-toggle__btn ${modalAssignKind === ASSIGN_KIND.EVENT ? "is-active" : ""}`}
                  onClick={() => setModalAssignKind(ASSIGN_KIND.EVENT)}
                >
                  Evento
                  {Boolean(modalForm.eventId) && (
                    <span className="roles-kind-toggle__dot" title="Con evento asignado" />
                  )}
                </button>
                <button
                  type="button"
                  className={`roles-kind-toggle__btn ${modalAssignKind === ASSIGN_KIND.RESERVA ? "is-active" : ""}`}
                  onClick={() => setModalAssignKind(ASSIGN_KIND.RESERVA)}
                >
                  Reserva
                  {Boolean(modalForm.barId) && (
                    <span className="roles-kind-toggle__dot" title="Con local asignado" />
                  )}
                </button>
              </div>
            )}

            {isModalReservaAssign ? (
              <>
                <OrganizerPillSelect
                  placeholder="Asignar evento o reserva"
                  value={modalForm.barId}
                  onChange={(val) =>
                    setModalForm((prev) => ({ ...prev, barId: val ? String(val) : "" }))
                  }
                  options={venueOptions}
                  className="roles-input roles-select"
                />
                {modalForm.barId && (
                  <button
                    type="button"
                    className="roles-link-action"
                    onClick={() => setModalForm((prev) => ({ ...prev, barId: "" }))}
                  >
                    Quitar asignación de local
                  </button>
                )}
              </>
            ) : (
              <OrganizerPillSelect
                placeholder="Asignar evento o reserva"
                value={modalForm.eventId}
                onChange={(val) =>
                  setModalForm((prev) => ({ ...prev, eventId: val ? String(val) : "" }))
                }
                options={events
                  .map((ev) => {
                    const raw = ev?.id ?? ev?._id ?? ev?.eventid;
                    if (!raw) return null;
                    return { value: String(raw), label: ev?.name || "Evento sin nombre" };
                  })
                  .filter(Boolean)}
                className="roles-input roles-select"
              />
            )}

            {!isModalReservaAssign && (
              <label className="roles-toggle">
                <input
                  type="checkbox"
                  checked={modalAllowAllTypes}
                  onChange={(e) => setModalAllowAllTypes(e.target.checked)}
                />
                <span className="roles-toggle__track">
                  <span className="roles-toggle__thumb" />
                </span>
                <span className="roles-toggle__label">
                  {activeTab === TAB_KEYS.SCANNERS
                    ? "Permitir validar todas las entradas"
                    : "Permitir vender todos los tipos"}
                </span>
              </label>
            )}

            {!isModalReservaAssign && !modalAllowAllTypes && (
              <div className="roles-ticket-types">
                {modalTicketTypes.length === 0 ? (
                  <p className="roles-ticket-types__empty">
                    No hay tipos de entradas para este evento
                  </p>
                ) : (
                  modalTicketTypes.map((tt) => {
                    const rawId = String(tt._id ?? tt.id ?? tt?.ticketid ?? tt?.typeid);
                    if (!rawId) return null;
                    return (
                      <label key={`modal-${rawId}`} className="roles-ticket-type">
                        <input
                          type="checkbox"
                          checked={modalSelectedTypeIds.includes(rawId)}
                          onChange={(e) => {
                            const id = rawId;
                            setModalSelectedTypeIds((prev) =>
                              e.target.checked
                                ? [...new Set([...prev, id])]
                                : prev.filter((value) => value !== id)
                            );
                          }}
                        />
                        <span>
                          {tt.name}
                          {tt.zone || tt.gate
                            ? ` · ${[tt.zone, tt.gate].filter(Boolean).join(" / ")}`
                            : ""}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="roles-btn"
            onClick={handleModalSave}
            disabled={modalSaving}
          >
            {modalSaving ? "Guardando..." : "Guardar"}
          </button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default Sellers;
